import { Component } from 'preact';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { TEXT_PRIMARY, TEXT_BODY } from '../../lib/medievalTheme';
import { CloseButton, BackButton } from '../ui/navButtons';
import { playUI, UI } from '../../utils/arena/uiSounds';
import { CLASSES, getClass, isClassAvailable } from '../../utils/arena/classes';
import { chooseClass } from '../../utils/arena/skills';
import { t } from '../../localization/t';
import ClassSignFigure from './ClassSignFigure';

// The class picker. Five small illustrated signs below the sky. Hovering a
// class gathers its figure in the nebula; choosing one
// asks for a single confirming click and sends the oath to the server.
//
// Classes whose trees do not exist yet still reveal their nebula figure,
// but cannot be chosen. They still preview on hover so the
// player can read what is coming.

// The accent as an "r, g, b" triplet, and a whitened copy for light. The
// stylesheet builds its alphas with rgba() from these: the CSS pipeline
// drops color-mix() with a variable inside gradients and keyframes.
function accentTriplets(hex) {
  const n = parseInt(hex.slice(1), 16);
  const rgb = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  const light = rgb.map((v, i) => Math.round(v * 0.65 + [255, 246, 230][i] * 0.35));
  return { '--vh-class-accent-rgb': rgb.join(', '), '--vh-class-light-rgb': light.join(', ') };
}

function ClassSign({ cls, focused, selected, current, onHover, onSelect }) {
  const available = isClassAvailable(cls.key);
  return (
    <button
      type="button"
      className={`vh-class-sign ${focused ? 'vh-class-sign-focus' : ''} ${selected ? 'vh-class-sign-selected' : ''} ${current ? 'vh-class-sign-held' : ''} ${available ? '' : 'vh-class-sign-unavailable'}`}
      style={{ '--vh-class-accent': cls.accent, ...accentTriplets(cls.accent), width: '17cqw' }}
      onMouseEnter={() => onHover(cls.key)}
      onMouseLeave={() => onHover(null)}
      onFocus={() => onHover(cls.key)}
      onBlur={() => onHover(null)}
      onClick={() => onSelect(cls.key)}
      aria-pressed={selected}
      data-testid={`class-sign-${cls.key}`}
    >
      <div className="vh-class-sign-sky">
        <ClassSignFigure classKey={cls.key} accent={cls.accent} lit={focused || selected || current} />
      </div>
      <div className="vh-class-sign-name arena-heading">{t(cls.title)}</div>
      <div className="vh-class-sign-tagline">{t(cls.tagline)}</div>
      {current ? (
        <div className="vh-class-sign-soon vh-class-sign-current">{t('Your class')}</div>
      ) : !available ? (
        <div className="vh-class-sign-soon">{t('Not yet available')}</div>
      ) : null}
    </button>
  );
}

export default class ClassPickerScreen extends Component {
  state = { hoveredKey: null, selectedKey: null, choosing: false };

  componentWillUnmount() { this._unmounted = true; }

  handleHover = (key) => {
    if (key && key !== this.state.hoveredKey) playUI(UI.HOVER, { volume: 0.4 });
    this.setState({ hoveredKey: key });
    this.emitAccent(key, this.state.selectedKey);
  };

  handleSelect = (key) => {
    const available = isClassAvailable(key);
    playUI(available ? UI.CLICK : UI.CLICK_SOFT);
    // Browsing with a class already held: a sign with a tree opens as a
    // preview instead of being chosen.
    if (this.props.currentKey) {
      if (available) this.props.onPreview?.(key);
      return;
    }
    this.setState({ selectedKey: key });
    if (available) this.emitAccent(this.state.hoveredKey, key);
  };

  // The night tints toward whichever sign the eye is on: the hovered
  // one, or the chosen one once the pointer has left.
  emitAccent(hoveredKey, selectedKey) {
    const focus = getClass(hoveredKey || selectedKey);
    this.props.onAccentChange?.(focus?.accent || null);
    this.props.onFocusChange?.(focus?.key || null);
  }

  handleConfirm = async () => {
    const { selectedKey, choosing } = this.state;
    if (!selectedKey || choosing || !isClassAvailable(selectedKey)) return;
    this.setState({ choosing: true });
    try {
      const next = await chooseClass(selectedKey);
      playUI(UI.LEVEL_UP);
      if (!this._unmounted) this.setState({ choosing: false });
      this.props.onChosen?.(next);
    } catch (err) {
      console.error('Failed to choose class:', err);
      if (!this._unmounted) this.setState({ choosing: false });
      toast.error(t('Could not choose that class. Try again.'));
    }
  };

  render() {
    const { onClose, onBack, currentKey } = this.props;
    const { hoveredKey, selectedKey, choosing } = this.state;
    const browsing = Boolean(currentKey);
    const current = getClass(currentKey);
    const focusKey = hoveredKey || selectedKey;
    const focus = getClass(focusKey);
    const selected = getClass(selectedKey);
    const canConfirm = !browsing && Boolean(selected && isClassAvailable(selected.key));

    return (
      <div className="absolute inset-0 flex flex-col" data-testid="class-picker">
        {onBack ? (
          <div className="absolute left-12 top-10 z-10">
            <BackButton title={t('Back to your tree')} onClick={onBack} />
          </div>
        ) : null}
        <div className="absolute right-12 top-10 z-10">
          <CloseButton title={t('Close')} onClick={onClose} />
        </div>

        <div className="shrink-0 text-center pt-12">
          <h1
            className="arena-heading font-bold"
            style={{ color: TEXT_PRIMARY, fontSize: '2.125cqw', textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}
          >
            {browsing ? t('The Star Signs') : t('Choose your Class')}
          </h1>
          <p className="mt-1" style={{ color: TEXT_BODY, fontSize: '0.8125cqw' }}>
            {browsing
              ? t('You walk the path of the {name}.', { name: t(current.title) })
              : t('One class holds all your skill points. You may renounce it later, for a price.')}
          </p>
        </div>

        <div className="flex-1 min-h-0 flex items-end justify-center">
          {/* Stretched so every sign shares one height and the bars line up. */}
          <div className="flex items-stretch justify-center" style={{ gap: '1.5cqw', width: '92cqw' }}>
            {CLASSES.map((cls) => (
              <ClassSign
                key={cls.key}
                cls={cls}
                focused={focusKey === cls.key}
                selected={selectedKey === cls.key}
                current={currentKey === cls.key}
                onHover={this.handleHover}
                onSelect={this.handleSelect}
              />
            ))}
          </div>
        </div>

        {/* The calling of the sign under the eye, beside the great figure. */}
        <div className="vh-class-calling">
          <AnimatePresence mode="wait">
            {focus ? (
              <motion.div
                key={focus.key}
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 4 }}
                transition={{ duration: 0.18, ease: [0.22, 0.8, 0.36, 1] }}
                style={{ '--vh-class-accent': focus.accent }}
              >
                <div className="vh-class-sign-name arena-heading">{t(focus.title)}</div>
                <div className="vh-class-rule" />
                {focus.summary.map((line) => (
                  <p key={line} className="vh-class-summary-line" style={{ color: TEXT_PRIMARY }}>{t(line)}</p>
                ))}
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>

        {!browsing ? (
          <div className="shrink-0 flex items-center justify-center" style={{ height: '3.5cqw', margin: '1cqw 0 1.5cqw' }}>
            <AnimatePresence>
              {canConfirm ? (
                <motion.button
                  key="confirm"
                  type="button"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.16 }}
                  className="vh-class-confirm"
                  style={{ '--vh-class-accent': selected.accent }}
                  disabled={choosing}
                  onClick={this.handleConfirm}
                  data-testid="class-confirm"
                >
                  {choosing ? t('Swearing…') : t('Become a {name}', { name: t(selected.title) })}
                </motion.button>
              ) : null}
            </AnimatePresence>
          </div>
        ) : (
          <div className="shrink-0" style={{ height: '1.5cqw' }} />
        )}
      </div>
    );
  }
}
